import { F } from '@/utils/intl';
import React, { useState } from 'react';
import { Button, Empty, Space, Spin, Tooltip } from 'antd';
import { useDispatch } from 'umi';
import { EyeInvisibleOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { LeadTaskModel } from '@/typings/models/LeadTask';
import TaskDesignRow from '@/components/Tasks/TaskDesignRow';

import rowStyles from '@/components/Tasks/TaskDesignRow.less';
import styles from './index.less';

type ITasksTableProps = {
  data: LeadTaskModel[];
  request: () => void;
  loading?: boolean;
  leadId?: string;
  filterColumns?: (column) => boolean;
  hideCreate?: boolean;
  hideActionText?: boolean;
  hideShowDone?: boolean;
  showLead?: boolean;
  showAssignee?: boolean;
  showDescription?: boolean;
}

export function TasksList({
                            data,
                            request,
                            loading,
                            leadId,
                            filterColumns = (column) => true,
                            hideCreate,
                            showLead,
                            hideShowDone,
                            showAssignee,
                          }: ITasksTableProps) {
  const [showDone, setShowDone] = useState(false)
  const dispatch = useDispatch()
  const toggleShowDone = () => setShowDone(prev => !prev)

  const tasks = showDone ? data : data.filter(task => !task.is_completed && !task.retired_at)
  const sortedTasks = [...tasks].sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))
  // Derive visibility flags from legacy filterColumns when explicit props are not provided
  const derivedShowLead = typeof showLead === 'boolean' ? showLead : filterColumns({ dataIndex: 'lead' });
  const derivedShowAssignee = typeof showAssignee === 'boolean' ? showAssignee : filterColumns({ dataIndex: 'assignee' });

  return (
    <Spin spinning={loading || false}>
      <div className={styles.tasksListContainer}>
        <Space className={styles.tasksListHeaderActions}>
          {!hideShowDone && (
            <Tooltip
              placement={'left'}
              title={
                <F id="pages.lead.tasks.showOpen" defaultMessage="Show Open" />
              }
            >
              <Button
                onClick={toggleShowDone}
                size={'small'}
                icon={showDone ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              />
            </Tooltip>
          )}
          {!hideCreate && (
            <Button
              type="primary"
              size={'small'}
              icon={<PlusOutlined />}
              onClick={() =>
                dispatch({
                  type: 'leadTasks/openCreateTaskDrawer',
                  payload: { task: null, leadId, onTaskCreated: request },
                })
              }
            />
          )}
        </Space>
        {/* Same task row as the tasks page. hideProject when the lead is
            already the surrounding context; compact drops the assignee stack. */}
        <div className={rowStyles.compactList}>
          {sortedTasks.map((task) => (
            <TaskDesignRow
              key={task.id}
              task={task}
              request={request}
              hideProject={!derivedShowLead}
              compact={!derivedShowAssignee}
            />
          ))}
          {sortedTasks.length === 0 && !loading && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<F id="pages.lead.tasks.empty" defaultMessage="No open tasks." />}
            />
          )}
        </div>
      </div>
    </Spin>
  );
}
